'use client'

import React, { memo, useCallback, useRef, useState } from 'react'

import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineArrowDownTray,
} from 'react-icons/hi2'
import { PiMicrosoftExcelLogo } from 'react-icons/pi'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import Button from '@/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import Spinner from '@/components/common/Spinner'

import { API_URL } from '@/constants/env'

import { useRecipientSearchStore } from '@/stores/RecipientSearchStore'

import type {
  RecipientExportFormat,
  RecipientSearchFilters,
  RecipientSearchResponse,
  RecipientSearchResult,
} from '@/types/recipient-search'

// =============================================================================
// CONSTANTS
// =============================================================================

const MAX_EXPORT_ROWS = 3000

// =============================================================================
// COLUMN DEFINITIONS
// =============================================================================

interface ExportColumn {
  key: keyof RecipientSearchResult | string
  label: string
  default: boolean
  format?: 'currency' | 'bool' | 'list'
}

const EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'name', label: 'Company Name', default: true },
  { key: 'uei', label: 'UEI', default: true },
  { key: 'duns', label: 'DUNS', default: true },
  { key: 'cage_code', label: 'CAGE Code', default: true },
  {
    key: 'last_12_months',
    label: 'Award Total (Last 12 Mo)',
    default: true,
    format: 'currency',
  },
  {
    key: 'last_12_award_count',
    label: 'Awards (Last 12 Months)',
    default: true,
  },
  { key: 'last_award_date', label: 'Last Award Date', default: true },
  {
    key: 'total_amount',
    label: 'All Time Total',
    default: true,
    format: 'currency',
  },
  { key: 'naics_codes', label: 'NAICS Codes', default: true, format: 'list' },
  { key: 'psc_codes', label: 'PSC Codes', default: true, format: 'list' },
  {
    key: 'alternate_names',
    label: 'Alternate Names',
    default: false,
    format: 'list',
  },
  { key: 'state', label: 'State', default: true },
  { key: 'city', label: 'City', default: true },
  { key: 'zip5', label: 'ZIP Code', default: false },
  {
    key: 'congressional_district',
    label: 'Congressional District',
    default: false,
  },
  { key: 'country_code', label: 'Country', default: false },
  {
    key: 'agencies',
    label: 'Awarding Agencies',
    default: true,
    format: 'list',
  },
  {
    key: 'last_12_contracts',
    label: 'Contracts (Last 12 Mo)',
    default: false,
    format: 'currency',
  },
  {
    key: 'last_12_grants',
    label: 'Grants (Last 12 Mo)',
    default: false,
    format: 'currency',
  },
  { key: 'total_awards', label: 'Total Awards (All Time)', default: false },
  { key: 'first_award_date', label: 'First Award Date', default: false },
  {
    key: 'small_business',
    label: 'Small Business',
    default: true,
    format: 'bool',
  },
  { key: 'woman_owned', label: 'Woman-Owned', default: true, format: 'bool' },
  {
    key: 'veteran_owned',
    label: 'Veteran-Owned',
    default: false,
    format: 'bool',
  },
  {
    key: 'sdvosb',
    label: 'Service-Disabled Veteran',
    default: false,
    format: 'bool',
  },
  { key: 'is_8a', label: '8(a) Program', default: true, format: 'bool' },
  { key: 'hubzone', label: 'HUBZone', default: true, format: 'bool' },
  {
    key: 'sdb',
    label: 'Small Disadvantaged Business',
    default: false,
    format: 'bool',
  },
  {
    key: 'minority_owned',
    label: 'Minority-Owned',
    default: false,
    format: 'bool',
  },
  {
    key: 'alaska_native',
    label: 'Alaska Native Corp',
    default: false,
    format: 'bool',
  },
  {
    key: 'native_american',
    label: 'Native American Owned',
    default: false,
    format: 'bool',
  },
  { key: 'nonprofit', label: 'Nonprofit', default: false, format: 'bool' },
  {
    key: 'educational',
    label: 'Educational Institution',
    default: false,
    format: 'bool',
  },
  { key: 'parent_name', label: 'Parent Company', default: false },
  { key: 'subsidiary_count', label: 'Subsidiary Count', default: false },
  { key: 'recipient_hash', label: 'Recipient ID', default: false },
]

// Pre-computed column key sets to avoid repeated filter/map iterations (js-combine-iterations)
const DEFAULT_COLUMN_KEYS: Set<string> = new Set(
  EXPORT_COLUMNS.reduce<string[]>((acc, col) => {
    if (col.default) acc.push(col.key)
    return acc
  }, []),
)

const ALL_COLUMN_KEYS: Set<string> = new Set(
  EXPORT_COLUMNS.map((col) => col.key),
)

// =============================================================================
// VALUE FORMATTING
// =============================================================================

function formatValue(
  value: unknown,
  format?: 'currency' | 'bool' | 'list',
): string | number {
  if (value === null || value === undefined) {
    return ''
  }

  if (format === 'currency') {
    if (typeof value === 'number') {
      return value // Keep as number for Excel formatting
    }
    return String(value)
  }

  if (format === 'bool') {
    return value ? 'Yes' : 'No'
  }

  if (format === 'list') {
    if (Array.isArray(value)) {
      return value.slice(0, 20).join('; ')
    }
    return String(value)
  }

  return String(value)
}

// =============================================================================
// FILENAME GENERATION
// =============================================================================

function generateFilename(
  format: 'csv' | 'xlsx',
  query?: string | null,
): string {
  const timestamp = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
  const base = query
    ? `recipients-${query.slice(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}`
    : 'recipients'
  return `${base}-${timestamp}.${format}`
}

// =============================================================================
// EXPORT FUNCTIONS
// =============================================================================

function exportToCSV(
  results: RecipientSearchResult[],
  columns: ExportColumn[],
  filename: string,
): void {
  const headers = columns.map((col) => col.label)
  const rows = results.map((result) =>
    columns.map((col) => {
      const value = result[col.key as keyof RecipientSearchResult]
      return formatValue(value, col.format)
    }),
  )

  // Build CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row
        .map((cell) => {
          const str = String(cell)
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`
          }
          return str
        })
        .join(','),
    ),
  ].join('\n')

  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

async function exportToXLSX(
  results: RecipientSearchResult[],
  columns: ExportColumn[],
  filename: string,
): Promise<void> {
  // Dynamic import exceljs to avoid SSR issues
  const ExcelJS = await import('exceljs')
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Recipients')

  // Add headers
  const headers = columns.map((col) => col.label)
  worksheet.addRow(headers)

  // Style header row
  const headerRow = worksheet.getRow(1)
  headerRow.font = { bold: true }
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE9E9E9' },
  }

  // Add data rows
  for (const result of results) {
    const row = columns.map((col) => {
      const value = result[col.key as keyof RecipientSearchResult]
      return formatValue(value, col.format)
    })
    worksheet.addRow(row)
  }

  // Auto-size columns (approximate)
  worksheet.columns.forEach((column, i) => {
    const maxLength = Math.max(
      headers[i].length,
      ...results.map((result) => {
        const value = result[columns[i].key as keyof RecipientSearchResult]
        return String(formatValue(value, columns[i].format)).length
      }),
    )
    column.width = Math.min(maxLength + 2, 50)
  })

  // Generate and download
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// =============================================================================
// DATA FETCHING
// =============================================================================

interface FetchResult {
  results: RecipientSearchResult[]
  limitReached: boolean
}

// Boolean filter fields that can be inferred from active filters
const BOOLEAN_FILTER_FIELDS = [
  'woman_owned',
  'veteran_owned',
  'sdvosb',
  'is_8a',
  'hubzone',
  'sdb',
  'minority_owned',
  'small_business',
  'alaska_native',
  'native_american',
  'nonprofit',
  'educational',
] as const

/**
 * Fill in boolean fields based on active filters.
 * If a filter is set to true, all results must have that value,
 * so we can safely infer it even if the backend didn't return it.
 */
function inferBooleanFieldsFromFilters(
  results: RecipientSearchResult[],
  filters: Partial<RecipientSearchFilters>,
): RecipientSearchResult[] {
  // Find which boolean filters are active (set to true)
  const activeFilters = BOOLEAN_FILTER_FIELDS.filter(
    (field) => filters[field] === true,
  )

  if (activeFilters.length === 0) {
    return results
  }

  // For each result, fill in the boolean fields if they're null/undefined
  return results.map((result) => {
    const updated = { ...result }
    for (const field of activeFilters) {
      if (updated[field] === null || updated[field] === undefined) {
        // If we filtered for this field=true, all results must be true
        ;(updated as Record<string, unknown>)[field] = true
      }
    }
    return updated
  })
}

async function fetchAllResults(
  token: string,
  filters: Partial<RecipientSearchFilters>,
  maxResults: number,
): Promise<FetchResult> {
  const allResults: RecipientSearchResult[] = []
  let cursor: string | null = null
  let limitReached = false
  const batchSize = 500 // Larger batches for faster export

  // For export, use attribute sorting (spending_desc) when no query
  // Both sort types use cursor-based pagination with a 3000 result cap
  const exportSort =
    filters.query || filters.sort !== 'relevance'
      ? filters.sort
      : 'spending_desc'

  while (allResults.length < maxResults) {
    const remaining = maxResults - allResults.length
    const limit = Math.min(batchSize, remaining)

    const res = await fetch(`${API_URL}/api/v1/recipients/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...filters,
        sort: exportSort,
        limit,
        cursor, // Use cursor-based pagination
        personalized: false, // Disable personalization for reliable pagination
      }),
    })

    if (!res.ok) {
      throw new Error(`Failed to fetch recipients: ${res.status}`)
    }

    const data: RecipientSearchResponse = await res.json()
    allResults.push(...data.results)

    // Check if backend hit its pagination cap (only applies to BM25 relevance)
    if (data.limit_reached) {
      limitReached = true
      break
    }

    if (!data.has_more || !data.next_cursor) {
      break
    }

    cursor = data.next_cursor
  }

  // Infer boolean values from active filters (workaround for sparse data)
  const enrichedResults = inferBooleanFieldsFromFilters(allResults, filters)

  return { results: enrichedResults, limitReached }
}

// =============================================================================
// COLUMN SELECTOR (inline collapsible)
// =============================================================================

// Hoisted static JSX for chevron icons (rendering-hoist-jsx)
const ChevronUpIcon = <HiChevronUp className="w-4 h-4 text-primary-400" />
const ChevronDownIcon = <HiChevronDown className="w-4 h-4 text-primary-400" />

interface ColumnSelectorProps {
  selectedColumns: Set<string>
  onToggle: (key: string) => void
  onSelectAll: () => void
  onSelectDefaults: () => void
  isExpanded: boolean
  onToggleExpanded: () => void
}

const ColumnSelector = memo(function ColumnSelector({
  selectedColumns,
  onToggle,
  onSelectAll,
  onSelectDefaults,
  isExpanded,
  onToggleExpanded,
}: ColumnSelectorProps) {
  // Event delegation for checkbox clicks - avoids creating N callbacks (js-combine-iterations)
  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const key = e.target.dataset.key
      if (key) onToggle(key)
    },
    [onToggle],
  )

  return (
    <div>
      {/* Collapsible header */}
      <button
        type="button"
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between px-2 py-1.5 text-sm text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800 rounded"
      >
        <span>Columns ({selectedColumns.size})</span>
        {isExpanded ? ChevronUpIcon : ChevronDownIcon}
      </button>

      {/* Collapsible content */}
      {isExpanded && (
        <div className="mt-1 border-t border-primary-100 dark:border-primary-800 pt-2">
          <div className="flex gap-2 px-2 mb-2">
            <button
              type="button"
              onClick={onSelectDefaults}
              className="text-xs text-accent-600 dark:text-accent-400 hover:underline"
            >
              Defaults
            </button>
            <span className="text-primary-300 dark:text-primary-600">|</span>
            <button
              type="button"
              onClick={onSelectAll}
              className="text-xs text-accent-600 dark:text-accent-400 hover:underline"
            >
              Select All
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {EXPORT_COLUMNS.map((col) => (
              <label
                key={col.key}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-primary-100 dark:hover:bg-primary-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  data-key={col.key}
                  checked={selectedColumns.has(col.key)}
                  onChange={handleCheckboxChange}
                  className="w-3.5 h-3.5 rounded border-primary-300 dark:border-primary-600 text-accent-600 focus:ring-accent-500"
                />
                <span className="text-xs text-primary-700 dark:text-primary-300">
                  {col.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
})

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface RecipientExportButtonProps {
  totalCount?: number
  className?: string
}

const RecipientExportButton: React.FC<RecipientExportButtonProps> = ({
  totalCount,
  className,
}) => {
  const { accessToken } = useAuthInfo()
  const { filters, identifierType } = useRecipientSearchStore()

  // Selected columns state - initialized from pre-computed defaults (rerender-lazy-state-init)
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    () => new Set(DEFAULT_COLUMN_KEYS),
  )
  const [columnsExpanded, setColumnsExpanded] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // Ref to access current selectedColumns in handleExport without dependency (rerender-move-effect-to-event)
  const selectedColumnsRef = useRef(selectedColumns)
  selectedColumnsRef.current = selectedColumns

  const handleToggleColumn = useCallback((key: string) => {
    setSelectedColumns((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  // Use pre-computed sets instead of filter/map (js-combine-iterations)
  const handleSelectAll = useCallback(() => {
    setSelectedColumns(new Set(ALL_COLUMN_KEYS))
  }, [])

  const handleSelectDefaults = useCallback(() => {
    setSelectedColumns(new Set(DEFAULT_COLUMN_KEYS))
  }, [])

  const handleToggleColumnsExpanded = useCallback(() => {
    setColumnsExpanded((prev) => !prev)
  }, [])

  // Use ref to read selectedColumns at call time, reducing dependency array (rerender-move-effect-to-event)
  const handleExport = useCallback(
    async (format: RecipientExportFormat) => {
      if (!accessToken) {
        toast.error('Please log in to export')
        return
      }

      // Read current columns from ref at call time instead of depending on state
      const currentSelectedColumns = selectedColumnsRef.current
      const columns: ExportColumn[] = []
      for (const col of EXPORT_COLUMNS) {
        if (currentSelectedColumns.has(col.key)) {
          columns.push(col)
        }
      }

      if (columns.length === 0) {
        toast.error('No columns selected')
        return
      }

      setIsExporting(true)

      try {
        // Build filters matching what the user sees
        const exportFilters = {
          ...filters,
          identifier_type: identifierType ?? undefined,
        }

        // Fetch all results (up to cap)
        const exportCount = Math.min(
          totalCount ?? MAX_EXPORT_ROWS,
          MAX_EXPORT_ROWS,
        )
        toast.info(`Fetching ${exportCount.toLocaleString()} results...`)

        const { results, limitReached } = await fetchAllResults(
          accessToken,
          exportFilters,
          exportCount,
        )

        if (results.length === 0) {
          toast.error('No results to export')
          return
        }

        // Generate filename with query context
        const filename = generateFilename(format, filters.query)

        // Export
        if (format === 'csv') {
          exportToCSV(results, columns, filename)
        } else {
          await exportToXLSX(results, columns, filename)
        }

        // Show success with limit warning if applicable
        const message = `Exported ${results.length.toLocaleString()} recipients to ${format.toUpperCase()}`
        if (limitReached && totalCount && results.length < totalCount) {
          toast.warning(message, {
            description: `Export capped at ${MAX_EXPORT_ROWS.toLocaleString()} rows. Total results: ${totalCount.toLocaleString()}`,
          })
        } else {
          toast.success(message)
        }
      } catch (error) {
        toast.error('Failed to export', {
          description: error instanceof Error ? error.message : 'Unknown error',
        })
      } finally {
        setIsExporting(false)
      }
    },
    [accessToken, filters, identifierType, totalCount],
  )

  const hasResults = totalCount !== undefined && totalCount > 0
  const exportCount = Math.min(totalCount ?? 0, MAX_EXPORT_ROWS)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline-compact"
          disabled={isExporting || !hasResults || selectedColumns.size === 0}
          className={twMerge(
            'h-9 px-3 gap-1.5 rounded-md text-xs font-medium',
            'bg-white dark:bg-primary-800',
            'border-primary-200 dark:border-primary-700',
            'hover:border-primary-300 dark:hover:border-primary-600',
            className,
          )}
        >
          {isExporting ? (
            <Spinner className="w-4 h-4" />
          ) : (
            <HiOutlineArrowDownTray className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">Export</span>
          <HiChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-primary-500">
          Export Format
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => handleExport('xlsx')}
          className="flex items-center gap-2 cursor-pointer"
        >
          <PiMicrosoftExcelLogo className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span>Excel (.xlsx)</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport('csv')}
          className="flex items-center gap-2 cursor-pointer"
        >
          <HiOutlineArrowDownTray className="w-4 h-4 text-primary-500" />
          <span>CSV (.csv)</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {/* Collapsible columns section */}
        <div className="px-1 py-1">
          <ColumnSelector
            selectedColumns={selectedColumns}
            onToggle={handleToggleColumn}
            onSelectAll={handleSelectAll}
            onSelectDefaults={handleSelectDefaults}
            isExpanded={columnsExpanded}
            onToggleExpanded={handleToggleColumnsExpanded}
          />
        </div>
        {totalCount !== undefined && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-primary-400 dark:text-primary-500">
              {exportCount.toLocaleString()} rows
              {totalCount > MAX_EXPORT_ROWS && (
                <span className="text-amber-500"> (capped)</span>
              )}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default memo(RecipientExportButton)
